import type { GetRef, InputRef, TableProps } from 'antd';
import {
  Button,
  Form,
  Input,
  message,
  Popconfirm,
  Skeleton,
  Table,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useContext, useEffect, useRef, useState } from 'react';
import React from 'react';
import type {
  CompetencyTable,
  CompetencyTableItem,
} from '../../Types/interfaces';
import {
  AddRowToCompetencyTable,
  DeleteRowCompetencyTable,
  GenerateInternshipConventionDocument,
  GetCompetencyTableByProcessId,
  UpdateRowCompetencyTable,
} from '../../helper/apiHelper';
import { FaTrashAlt } from 'react-icons/fa';

type Item = CompetencyTableItem & { key: React.Key };

type FormInstance<T> = GetRef<typeof Form<T>>;

const EditableContext =
  React.createContext<FormInstance<CompetencyTableItem> | null>(null);

const EditableRow = ({ ...props }): React.ReactElement => {
  const [form] = Form.useForm();
  return (
    <Form form={form} component={false}>
      <EditableContext.Provider value={form}>
        <tr {...props} />
      </EditableContext.Provider>
    </Form>
  );
};

interface EditableCellProps {
  title: React.ReactNode;
  editable: boolean;
  dataIndex: keyof Item;
  record: Item;
  handleSave: (record: Item) => void;
}

const EditableCell: React.FC<React.PropsWithChildren<EditableCellProps>> = ({
  title: _title,
  editable,
  children,
  dataIndex,
  record,
  handleSave,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<InputRef>(null);
  const form = useContext(EditableContext);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
    }
  }, [editing]);

  const toggleEdit = (): void => {
    setEditing(!editing);
    form?.setFieldsValue({ [dataIndex]: record[dataIndex] });
  };

  const save = async (): Promise<void> => {
    try {
      if (!form) return;
      const values = await form.validateFields();

      toggleEdit();
      handleSave({ ...record, ...values });
    } catch (errInfo) {
      console.log('Save failed:', errInfo);
    }
  };

  let childNode = children;

  if (editable) {
    childNode = editing ? (
      <Form.Item style={{ margin: 0 }} name={dataIndex}>
        <Input.TextArea
          style={{
            width: '100%',
            wordBreak: 'break-word',
          }}
          ref={inputRef}
          onPressEnter={save}
          onBlur={save}
        />
      </Form.Item>
    ) : (
      <div
        className="editable-cell-value-wrap"
        style={{
          minHeight: 32,
          wordBreak: 'break-word',
          cursor: 'pointer',
        }}
        onClick={toggleEdit}
      >
        {children}
      </div>
    );
  }

  return <td {...restProps}>{childNode}</td>;
};

type ColumnTypes = Exclude<TableProps<Item>['columns'], undefined>;

const components = {
  body: {
    row: EditableRow,
    cell: EditableCell,
  },
};

interface EditableCompetencyTableProps {
  defaultCompetencyTable?: CompetencyTable;
  setDefaultCompetencyTable?: (table: CompetencyTable) => void;
  showAddRowButton?: boolean;
  processId?: number;
  isEditableTable?: boolean;
}

export const EditableCompetencyTable = ({
  defaultCompetencyTable,
  setDefaultCompetencyTable,
  processId,
  isEditableTable = false,
}: EditableCompetencyTableProps): React.ReactElement => {
  const { t } = useTranslation();
  const [dataSource, setDataSource] = useState<Item[]>();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [competencyTableId, setCompetencyTableId] = useState<number | null>(
    null
  );
  const [messageApi, contextHolder] = message.useMessage();

  const updateDataSource = (newData: Item[]): Item[] => {
    const sortedData = [...newData].sort((a, b) => Number(a.nr) - Number(b.nr));
    setDataSource(sortedData);
    return sortedData;
  };

  useEffect(() => {
    const fetchDataIfNotExist = async (): Promise<void> => {
      try {
        let competencyTable = defaultCompetencyTable ?? null;
        if (!defaultCompetencyTable && processId) {
          competencyTable = await GetCompetencyTableByProcessId(processId);
          setCompetencyTableId(competencyTable.id ?? null);
        }

        if (competencyTable) {
          const items: Item[] =
            competencyTable.competencyTableItems
              .map((item) => ({
                ...item,
                key: item.nr,
              }))
              .sort((a, b) => Number(a.nr) - Number(b.nr)) ?? [];
          updateDataSource(items);
        }
      } catch {
        messageApi.error(t('editableCompetenceTable.notLoadedTable'));
      } finally {
        setIsLoading(false);
      }
    };

    fetchDataIfNotExist();
  }, [defaultCompetencyTable, messageApi, processId, t]);

  const updateIfExistDefaultCompTable = (newData: Item[]): void => {
    // the return value it is necessary, because the set method is asynchronous and does not update in time to use bellow
    const freshDataSource = updateDataSource(newData);
    setDefaultCompetencyTable?.({
      competencyTableItems:
        freshDataSource.map(({ key: _key, ...rest }) => rest) ?? [],
    });
  };

  const handleAdd = async (): Promise<void> => {
    if (!dataSource) return;
    const newKey =
      dataSource.length > 0
        ? Math.max(...dataSource.map((d) => Number(d.key))) + 1
        : 1;
    const newRow: CompetencyTableItem = {
      nr: newKey,
      competency: '',
      trainingModule: '',
      workLocation: '',
      plannedActivities: '',
      observations: '',
    };
    const newData: Item = {
      ...newRow,
      key: newKey,
    };

    if (!competencyTableId) {
      updateIfExistDefaultCompTable([...dataSource, newData]);
      return;
    }

    try {
      if (!processId) return;
      const response: CompetencyTableItem = await AddRowToCompetencyTable(
        competencyTableId,
        newRow
      );
      await GenerateInternshipConventionDocument(processId);
      const newDataWithId: Item = {
        ...newData,
        id: response.id,
      };
      updateDataSource([...dataSource, newDataWithId]);
      messageApi.success(t('editableCompetenceTable.successAddRow'));
    } catch {
      messageApi.error(t('editableCompetenceTable.errorWhenAddNewRow'));
    }
  };

  const handleDelete = async (key: React.Key): Promise<void> => {
    if (!dataSource) return;
    const newData = dataSource?.filter((item) => item.key !== key);
    const itemToDelete = dataSource.find((item) => item.key === key);

    updateDataSource(newData);

    if (!competencyTableId) {
      updateIfExistDefaultCompTable(newData);
      return;
    }

    try {
      if (!itemToDelete || !itemToDelete.id || !processId) return;
      await DeleteRowCompetencyTable(competencyTableId, itemToDelete.id);
      await GenerateInternshipConventionDocument(processId);
      message.success(t('editableCompetenceTable.successDeleteRow'));
    } catch {
      messageApi.error(t('editableCompetenceTable.errorWhenDeleteRow'));
    }
  };

  const handleSave = async (row: Item): Promise<void> => {
    const oldData = [...(dataSource ?? [])];
    const newData = [...oldData];
    const index = newData.findIndex((item) => row.key === item.key);
    const item = newData[index];

    newData.splice(index, 1, {
      ...item,
      ...row,
    });
    updateDataSource(newData);

    if (!competencyTableId) {
      updateIfExistDefaultCompTable(newData);
      return;
    }

    try {
      const mergedRow = { ...item, ...row };
      const updateRow: CompetencyTableItem = {
        nr: mergedRow.nr,
        competency: mergedRow.competency,
        trainingModule: mergedRow.trainingModule,
        workLocation: mergedRow.workLocation,
        plannedActivities: mergedRow.plannedActivities,
        observations: mergedRow.observations,
      };

      if (!item || !item.id || !processId) return;
      await UpdateRowCompetencyTable(competencyTableId, item.id, updateRow);
      await GenerateInternshipConventionDocument(processId);
      messageApi.success(t('editableCompetenceTable.successSave'));
    } catch {
      updateDataSource(oldData);
      messageApi.error(t('editableCompetenceTable.errorWhenSaveRow'));
    }
  };

  const defaultColumns: (ColumnTypes[number] & {
    editable?: boolean;
    dataIndex: string;
  })[] = [
    {
      title: 'Nr.',
      dataIndex: 'nr',
      width: '1%',
      align: 'center',
      editable: isEditableTable,
      sorter: (a, b) => Number(a.nr) - Number(b.nr),
    },
    {
      title: 'Competența',
      dataIndex: 'competency',
      width: '25%',
      editable: isEditableTable,
    },
    {
      title: 'Modul de pregătire',
      dataIndex: 'trainingModule',
      width: '25%',
      editable: isEditableTable,
    },
    {
      title: 'Locul de muncă',
      dataIndex: 'workLocation',
      width: '15%',
      editable: isEditableTable,
    },
    {
      title: 'Activități planificate',
      dataIndex: 'plannedActivities',
      width: '25%',
      editable: isEditableTable,
    },
    {
      title: 'Observații',
      dataIndex: 'observations',
      width: '15%',
      editable: isEditableTable,
    },
    ...(isEditableTable
      ? [
          {
            title: t('editableCompetenceTable.operation'),
            width: '5%',
            align: 'center' as const,
            dataIndex: 'operation',
            render: (_: unknown, record: Item) =>
              dataSource && dataSource.length >= 1 ? (
                <Popconfirm
                  title="Sure to delete?"
                  onConfirm={() => handleDelete(record.key)}
                >
                  <Button type="text" danger>
                    <FaTrashAlt size={18} />
                  </Button>
                </Popconfirm>
              ) : null,
          },
        ]
      : []),
  ];

  const columns = defaultColumns.map((col) => {
    if (!col.editable) {
      return col;
    }
    return {
      ...col,
      onCell: (record: Item): Record<string, unknown> => ({
        record,
        editable: col.editable,
        dataIndex: col.dataIndex,
        title: col.title,
        handleSave,
      }),
    };
  });

  if (isLoading) {
    return (
      <div>
        <Skeleton active paragraph={{ rows: 10 }} />
      </div>
    );
  }

  return (
    <div>
      {contextHolder}
      {isEditableTable && (
        <Button onClick={handleAdd} type="primary" style={{ marginBottom: 16 }}>
          {t('newInternshipComp.editableCompetencyTable.addRow')}
        </Button>
      )}
      <Table<Item>
        components={components}
        scroll={{ x: 'max-content' }}
        rowClassName={() => 'editable-row'}
        bordered
        dataSource={dataSource}
        columns={columns as ColumnTypes}
        pagination={false}
      />
    </div>
  );
};
